import { Grid, Skeleton } from '@mui/material';
import { Field, Formik } from 'formik';
import { useSnackbar } from 'notistack';
import { useMutation, useQueryClient } from 'react-query';
import { useParams } from 'react-router-dom';
import {
	CerebroBreadcrumb,
	CerebroDropdown,
	CerebroDropdownMenuItem,
	CerebroForm,
	CerebroInput,
	CerebroLabel,
	CerebroLink,
	CerebroPageContainer,
	CerebroPrimaryButton,
	CerebroText,
	CerebroYesNo,
} from '../shared/components';
import { useApi, useAuth } from '../shared/context';
import {
	QueryKeys,
	useDataRoomQuery,
	useUserControlledObjectsQuery,
	useDealProductQuery,
	useLenderRelationsManagersQuery,
	useTransactionManagersQuery,
	useSecondaryTransactionManagersQuery,
	useDealQuery,
	useSalesRepsQuery,
} from '../shared/hooks';
import { DataRoom, DataRoomStatus, DataRoomStatusMap } from '../shared/models';
import { FormikUtils } from '../shared/utils';
import { CerebroApiEndpoints } from '../shared/utils/api';
import { getApiResponseData } from '../shared/utils/axios';
import { PlatformRootUrl } from '../shared/utils/platform';

export const Settings: React.FC = () => {
	const api = useApi();
	const { guid } = useParams<'guid'>();
	const client = useQueryClient();
	const snackbar = useSnackbar();
	const { currentUser } = useAuth();

	const dataRoomQuery = useDataRoomQuery(guid);
	const dealQuery = useDealQuery(dataRoomQuery.data?.dealGuid, {
		enabled: dataRoomQuery.isSuccess,
		retry: false,
	});

	const transactionManagerQuery = useTransactionManagersQuery(dealQuery.data?.transactionManager, {
		enabled: dataRoomQuery.isSuccess && dealQuery.isSuccess,
		retry: false,
	});
	const secondaryTransactionManagerQuery = useSecondaryTransactionManagersQuery(
		dealQuery.data?.secondaryTransactionManager,
		{
			enabled: dataRoomQuery.isSuccess && dealQuery.isSuccess,
			retry: false,
		}
	);
	const controlledObjectsQuery = useUserControlledObjectsQuery(currentUser.userGuid);
	const productQuery = useDealProductQuery(dataRoomQuery.data?.dealGuid ?? '', {
		enabled: dataRoomQuery.isSuccess,
		retry: false,
	});

	const salesRepQuery = useSalesRepsQuery(dealQuery.data?.salesPerson, {
		enabled: dealQuery.isSuccess,
		retry: false,
	});

	const navigateToDealStatusPage = () => {
		window.location.href = `${PlatformRootUrl}/Emp/UpdateDealStatus/${dataRoomQuery.data?.dealGuid}`;
	};

	const settingsMutation = useMutation(
		async ({ values }: SettingsMutationVariables) => {
			const { data, error } = await getApiResponseData<DataRoom>(
				api.patch(CerebroApiEndpoints.DataRooms.Root(guid), {
					page: 'Settings',
					...values,
					secondaryTransactionManager:
						values.secondaryTransactionManager == '-1' ? null : values.secondaryTransactionManager,
					transactionManager: values.transactionManager == '-1' ? null : values.transactionManager,
				})
			);

			if (error) {
				throw new Error(error.response?.data);
			}

			return data;
		},
		{
			onSuccess: (data, { helpers, values }) => {
				client.setQueryData(QueryKeys.DataRoom.Root(guid), data);
				snackbar.enqueueSnackbar('Data room settings updated.', { variant: 'success' });
				helpers.resetForm({
					values: {
						dealName: values.dealName,
						transactionManager: values.transactionManager ?? '-1',
						secondaryTransactionManager: values.secondaryTransactionManager ?? '-1',
						roomStatus: data?.roomStatus ?? 'Closed',
						useDataRoomReminders: data?.useDataRoomReminders ?? true,
						hubSpotId: data?.hubSpotId ?? '',
						outsideDataRoomUrl: data?.outsideDataRoomUrl ?? '',
						showFeedbackSummary: data?.showFeedbackSummary ?? false,
						isEditingFeedbackSummary: data?.isEditingFeedbackSummary ?? false,
						showActiveSummaryReport: data?.showActiveSummaryReport ?? false,
					} as SettingsFormValues,
				});
			},
			onError: (error: Error) => {
				snackbar.enqueueSnackbar(error.message, { variant: 'error' });
			},
			onSettled: (_data, _error, { helpers }) => {
				helpers.setSubmitting(false);
			},
		}
	);

	return (
		<CerebroPageContainer>
			<CerebroBreadcrumb>Settings</CerebroBreadcrumb>

			{dataRoomQuery.isLoading || dealQuery.isLoading || controlledObjectsQuery.isLoading ? (
				<Skeleton height={400} />
			) : (
				<Formik
					initialValues={
						{
							dealName: dealQuery.data?.dealName ?? '',
							transactionManager: dealQuery.data?.transactionManager ?? '-1',
							secondaryTransactionManager: dealQuery.data?.secondaryTransactionManager ?? '-1',
							salesPerson: dealQuery.data?.salesPerson ?? '-1',
							roomStatus: dataRoomQuery.data?.roomStatus ?? 'Closed',
							useDataRoomReminders: dataRoomQuery.data?.useDataRoomReminders ?? true,
							hubSpotId: dataRoomQuery.data?.hubSpotId ?? '',
							outsideDataRoomUrl: dataRoomQuery.data?.outsideDataRoomUrl ?? '',
							showFeedbackSummary: dataRoomQuery.data?.showFeedbackSummary ?? false,
							isEditingFeedbackSummary: dataRoomQuery.data?.isEditingFeedbackSummary ?? false,
							showActiveSummaryReport: dataRoomQuery.data?.showActiveSummaryReport ?? false,
						} as SettingsFormValues
					}
					onSubmit={FormikUtils.MutateOnSubmit(settingsMutation.mutate)}
				>
					{({ isSubmitting, handleSubmit }) => {
						const allowedToUpdateHubSpotId =
							currentUser.isEmployeeUser &&
							controlledObjectsQuery.isSuccess &&
							controlledObjectsQuery.data.filter(
								obj => obj.hasAccess != null && obj.hasAccess && obj.type == 'UpdateHubSpotId'
							).length > 0;

						return (
							<CerebroForm onSubmit={handleSubmit}>
								{dealQuery.isSuccess && dealQuery.data.hasLaunched && (
									<Grid container spacing={3} sx={{ marginTop: 3 }}>
										<Grid item md={6}>
											<CerebroLabel id="roomStatus">Data Room Status</CerebroLabel>
											{dataRoomQuery.isSuccess &&
												productQuery.isSuccess &&
												controlledObjectsQuery.isSuccess ? (
												<>
													{productQuery.data && (
														<>
															<Field
																id="roomStatus"
																as={CerebroDropdown}
																name="roomStatus"
																disabled={isSubmitting}
															>
																{Object.entries(DataRoomStatusMap).map(
																	([value, description]) => (
																		<CerebroDropdownMenuItem
																			key={value}
																			value={value}
																		>
																			{description}
																		</CerebroDropdownMenuItem>
																	)
																)}
															</Field>
															<CerebroText variant="hint">
																Automatically send reminder emails to providers that
																have not responded to teasers and data room invites
																(reminders are sent 2 days, 4 days, and 6 days after the
																initial teaser or invite).
															</CerebroText>
														</>
													)}
													{!productQuery.data && (
														<>
															<CerebroText variant="hint">
																A product must be associated to this deal prior to
																opening the data room. This can be done from the{' '}
																<CerebroLink
																	variant="link"
																	onClick={() => navigateToDealStatusPage()}
																>
																	Deal Status page
																</CerebroLink>
																.
															</CerebroText>
														</>
													)}
												</>
											) : (
												<Skeleton height={60} />
											)}
										</Grid>
									</Grid>
								)}

								<Grid container spacing={3} sx={{ marginTop: 3 }}>
									<Grid item md={6}>
										<CerebroLabel id="useDataRoomReminders">
											Send Teaser &amp; Invite Reminders
										</CerebroLabel>
										{dataRoomQuery.isSuccess && controlledObjectsQuery.isSuccess ? (
											<>
												<Field
													id="useDataRoomReminders"
													as={CerebroYesNo}
													name="useDataRoomReminders"
													disabled={isSubmitting}
												/>
												<CerebroText variant="hint">
													Automatically send reminder emails to providers that have not
													responded to teasers and data room invites (reminders are sent 2
													days, 4 days, and 6 days after the initial teaser or invite).
												</CerebroText>
											</>
										) : (
											<Skeleton height={60} />
										)}
									</Grid>
								</Grid>
								<Grid container spacing={3} sx={{ marginTop: 3 }}>
									<Grid item md={6}>
										<CerebroLabel id="showActiveSummaryReport">
											Show Active Summary Report
										</CerebroLabel>
										{dataRoomQuery.isSuccess && controlledObjectsQuery.isSuccess ? (
											<>
												<Field
													id="showActiveSummaryReport"
													as={CerebroYesNo}
													name="showActiveSummaryReport"
													disabled={isSubmitting}
												/>
												<CerebroText variant="hint">
													Select yes to allow the borrower to view the active summary report.
												</CerebroText>
											</>
										) : (
											<Skeleton height={60} />
										)}
									</Grid>
								</Grid>
								<Grid container spacing={3} sx={{ marginTop: 3 }}>
									<Grid item md={6}>
										<CerebroLabel id="showFeedbackSummary">
											Show Feedback Summary Report
										</CerebroLabel>
										{dataRoomQuery.isSuccess && controlledObjectsQuery.isSuccess ? (
											<>
												<Field
													id="showFeedbackSummary"
													as={CerebroYesNo}
													name="showFeedbackSummary"
													disabled={isSubmitting}
												/>
												<CerebroText variant="hint">
													Select yes to allow the borrower to view the feedback summary
													report.
												</CerebroText>
											</>
										) : (
											<Skeleton height={60} />
										)}
									</Grid>
								</Grid>
								<Grid container spacing={3} sx={{ marginTop: 3 }}>
									<Grid item md={6}>
										<CerebroLabel id="isEditingFeedbackSummary">
											Editing Feedback Summary Report
										</CerebroLabel>
										{dataRoomQuery.isSuccess && controlledObjectsQuery.isSuccess ? (
											<>
												<Field
													id="isEditingFeedbackSummary"
													as={CerebroYesNo}
													name="isEditingFeedbackSummary"
													disabled={isSubmitting}
												/>
												<CerebroText variant="hint">
													This setting when set to Yes will show the editing features on the
													the Feedback Summary report page for employee users. Anything hidden
													from borrowers will be displayed with elements showing they are
													hidden. When set to No, the Feedback Summary report page will show
													the same for employee and borrower users. Anything hidden will not
													be shown and no editing features will be present for any user
													visiting the page.
												</CerebroText>
											</>
										) : (
											<Skeleton height={60} />
										)}
									</Grid>
								</Grid>

								{allowedToUpdateHubSpotId && (
									<Grid container spacing={3} sx={{ marginTop: 3 }}>
										<Grid item md={6}>
											<CerebroLabel id="hubSpotId">HubSpot Deal ID</CerebroLabel>
											{dataRoomQuery.isSuccess && controlledObjectsQuery.isSuccess ? (
												<>
													<Field
														id="hubSpotId"
														as={CerebroInput}
														name="hubSpotId"
														placeholder="HubSpot ID"
														disabled={isSubmitting}
													/>
													<CerebroText variant="hint">Deal ID within HubSpot.</CerebroText>
												</>
											) : (
												<Skeleton height={60} />
											)}
										</Grid>
									</Grid>
								)}

								<Grid container spacing={3} sx={{ marginTop: 3 }}>
									<Grid item md={6}>
										<CerebroLabel id="dealName">Deal Name</CerebroLabel>
										{dataRoomQuery.isSuccess && (
											<>
												<Field
													id="dealName"
													as={CerebroInput}
													name="dealName"
													placeholder="Name of the deal..."
													disabled={isSubmitting}
												/>
											</>
										)}
									</Grid>
								</Grid>

								<Grid container spacing={3} sx={{ marginTop: 3 }}>
									<Grid item md={6}>
										<CerebroLabel id="transactionManager">Transaction Manager</CerebroLabel>
										{dealQuery.isSuccess && transactionManagerQuery.isSuccess ? (
											<>
												<Field
													id="transactionManager"
													as={CerebroDropdown}
													name="transactionManager"
													disabled={isSubmitting}
												>
													<CerebroDropdownMenuItem value="-1">
														Unassigned
													</CerebroDropdownMenuItem>
													{transactionManagerQuery.data.map(manager => (
														<CerebroDropdownMenuItem
															key={manager.guid + 'primary'}
															value={manager.guid}
														>
															{manager.name}
														</CerebroDropdownMenuItem>
													))}
												</Field>
											</>
										) : (
											<Skeleton height={60} />
										)}
									</Grid>
								</Grid>

								<Grid container spacing={3} sx={{ marginTop: 3 }}>
									<Grid item md={6}>
										<CerebroLabel id="secondaryTransactionManager">
											Secondary Transaction Manager
										</CerebroLabel>
										{dealQuery.isSuccess && secondaryTransactionManagerQuery.isSuccess ? (
											<>
												<Field
													id="secondaryTransactionManager"
													as={CerebroDropdown}
													name="secondaryTransactionManager"
													disabled={isSubmitting}
												>
													<CerebroDropdownMenuItem value="-1">
														Unassigned
													</CerebroDropdownMenuItem>
													{secondaryTransactionManagerQuery.data.map(manager => (
														<CerebroDropdownMenuItem
															key={manager.guid + 'secondary'}
															value={manager.guid}
														>
															{manager.name}
														</CerebroDropdownMenuItem>
													))}
												</Field>
											</>
										) : (
											<Skeleton height={60} />
										)}
									</Grid>
								</Grid>

								<Grid container spacing={3} sx={{ marginTop: 3 }}>
									<Grid item md={6}>
										<CerebroLabel>
											Sales Rep
										</CerebroLabel>
										{salesRepQuery.isSuccess ? (
											<Field
												id="SalesRep"
												name="SalesRep"
												as={CerebroInput}
												disabled={true}
												value={salesRepQuery.data.find(
													m => m.guid === dealQuery.data?.salesPerson
												)?.name || "Unassigned"}
												sx={{
													backgroundColor: 'transparent',
													color: 'inherit',
													opacity: 1,
													pointerEvents: 'none',
												}}
											/>) : (
											<Skeleton height={60} />
										)}
									</Grid>
								</Grid>

								<Grid
									container
									sx={{ paddingBottom: 2, marginTop: 6 }}
									justifyContent={{ xs: 'center', md: 'flex-start' }}
								>
									<Grid item>
										<CerebroPrimaryButton loading={isSubmitting} type="submit">
											Save Settings
										</CerebroPrimaryButton>
									</Grid>
								</Grid>
							</CerebroForm>
						);
					}}
				</Formik>
			)}
		</CerebroPageContainer>
	);
};

type SettingsMutationVariables = FormikUtils.MutateOnSubmitVariables<SettingsFormValues>;
interface SettingsFormValues {
	roomStatus: DataRoomStatus;
	useDataRoomReminders: boolean;
	hubSpotId: string;
	outsideDataRoomUrl: string;
	dealName: string;
	transactionManager: string;
	secondaryTransactionManager: string;
	showFeedbackSummary: boolean;
	isEditingFeedbackSummary: boolean;
	showActiveSummaryReport: boolean;
}